import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    CircularProgress,
    Stack,
    Chip,
    Paper,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
} from "@mui/material";
import {
    Memory as MemoryIcon,
    Storage as StorageIcon,
    Speed as SpeedIcon,
    NetworkCheck as NetworkIcon,
    Computer as ComputerIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area
} from "recharts";

const PROMETHEUS_SERVERS = [
    { label: "MY-WSH - UAT", url: "http://54.251.245.235:9090" },
    { label: "MY_WSH - LIVE", url: "http://46.137.210.155:9090" },
    { label: "LMT - LIVE", url: "http://65.0.75.2:9090" },
    { label: "GRINNER", url: "http://13.233.240.198:9090" },
    { label: "RAYAL - LIVE", url: "http://13.235.130.207:9090" },
];

const SystemMonitoring = () => {
    const [selectedServer, setSelectedServer] = useState(PROMETHEUS_SERVERS[0].url);
    const [metrics, setMetrics] = useState({
        cpuUsage: 0,
        memoryUsage: 0,
        memoryTotal: 0,
        memoryAvailable: 0,
        diskUsage: 0,
        diskTotal: 0,
        diskAvailable: 0,
        networkReceive: 0,
        networkTransmit: 0,
        networkTransmit: 0,
        uptime: 0,
    });
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchPrometheusMetric = async (query) => {
        try {
            const response = await axios.get(`${selectedServer}/api/v1/query`, {
                params: { query },
                timeout: 5000,
            });
            if (response.data.status === "success" && response.data.data.result.length > 0) {
                return parseFloat(response.data.data.result[0].value[1]);
            }
            return 0;
        } catch (err) {
            console.error(`Error fetching metric ${query}:`, err);
            throw err;
        }
    };

    const fetchHistoryMetrics = async () => {
        try {
            const end = Math.floor(Date.now() / 1000);
            const start = end - (10 * 24 * 60 * 60); // 10 days ago
            const step = 3600 * 6; // 6 hours step

            const query = '100 - (avg(rate(node_cpu_seconds_total{mode="idle"}[5m])) * 100)';

            const response = await axios.get(`${selectedServer}/api/v1/query_range`, {
                params: {
                    query,
                    start,
                    end,
                    step
                },
                timeout: 10000,
            });

            if (response.data.status === "success" && response.data.data.result.length > 0) {
                const values = response.data.data.result[0].values;
                const formattedData = values.map(([timestamp, value]) => ({
                    time: new Date(timestamp * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                    fullTime: new Date(timestamp * 1000).toLocaleString(),
                    usage: parseFloat(value).toFixed(2)
                }));
                setHistoryData(formattedData);
            }
        } catch (err) {
            console.error("Error fetching history metrics:", err);
        }
    };

    const fetchMetrics = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch current metrics
            const cpuIdle = await fetchPrometheusMetric(
                'avg(rate(node_cpu_seconds_total{mode="idle"}[5m])) * 100'
            );
            const cpuUsage = 100 - cpuIdle;

            const memTotal = await fetchPrometheusMetric("node_memory_MemTotal_bytes");
            const memAvailable = await fetchPrometheusMetric("node_memory_MemAvailable_bytes");
            const memUsage = memTotal > 0 ? ((memTotal - memAvailable) / memTotal) * 100 : 0;

            const diskTotal = await fetchPrometheusMetric(
                'node_filesystem_size_bytes{mountpoint="/",fstype!="rootfs"}'
            );
            const diskAvail = await fetchPrometheusMetric(
                'node_filesystem_avail_bytes{mountpoint="/",fstype!="rootfs"}'
            );
            const diskUsage = diskTotal > 0 ? ((diskTotal - diskAvail) / diskTotal) * 100 : 0;

            const netReceive = await fetchPrometheusMetric(
                'rate(node_network_receive_bytes_total{device!="lo"}[5m])'
            );
            const netTransmit = await fetchPrometheusMetric(
                'rate(node_network_transmit_bytes_total{device!="lo"}[5m])'
            );

            const uptime = await fetchPrometheusMetric("node_time_seconds - node_boot_time_seconds");

            setMetrics({
                cpuUsage: cpuUsage.toFixed(2),
                memoryUsage: memUsage.toFixed(2),
                memoryTotal: (memTotal / 1024 / 1024 / 1024).toFixed(2),
                memoryAvailable: (memAvailable / 1024 / 1024 / 1024).toFixed(2),
                diskUsage: diskUsage.toFixed(2),
                diskTotal: (diskTotal / 1024 / 1024 / 1024).toFixed(2),
                diskAvailable: (diskAvail / 1024 / 1024 / 1024).toFixed(2),
                networkReceive: (netReceive / 1024).toFixed(2),
                networkTransmit: (netTransmit / 1024).toFixed(2),
                uptime: Math.floor(uptime / 86400),
            });

            // Fetch history
            await fetchHistoryMetrics();

            setLastUpdated(new Date());
            setLoading(false);
        } catch (err) {
            console.error("Error fetching metrics:", err);
            setError(`Failed to fetch metrics from ${PROMETHEUS_SERVERS.find(s => s.url === selectedServer)?.label || 'server'}`);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
    }, [selectedServer]);

    const handleRefresh = () => {
        fetchMetrics();
    };

    const handleServerChange = (event) => {
        setSelectedServer(event.target.value);
    };

    const getColorByUsage = (usage) => {
        if (usage < 50) return "#4caf50";
        if (usage < 75) return "#ff9800";
        return "#f44336";
    };

    const MetricCard = ({ title, icon, value, total, usage, unit = "%" }) => (
        <Card
            sx={{
                height: "100%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                },
            }}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: "rgba(255,255,255,0.2)",
                                borderRadius: "50%",
                                p: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {icon}
                        </Box>
                    </Stack>

                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {value}
                        <Typography component="span" variant="h5" sx={{ ml: 1, opacity: 0.8 }}>
                            {unit}
                        </Typography>
                    </Typography>

                    {total && (
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {total}
                        </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="caption">Usage</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {usage}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={parseFloat(usage)}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: "rgba(255,255,255,0.2)",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: getColorByUsage(parseFloat(usage)),
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    const InfoCard = ({ title, icon, value, subtitle, color }) => (
        <Card
            sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
                borderRadius: 3,
                border: `2px solid ${color}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            sx={{
                                backgroundColor: color,
                                borderRadius: "50%",
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                            }}
                        >
                            {icon}
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {title}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: color }}>
                                {value}
                            </Typography>
                            {subtitle && (
                                <Typography variant="caption" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 4, backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#2c3e50" }}>
                        System Monitoring Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Real-time server metrics powered by Prometheus
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Server</InputLabel>
                        <Select
                            value={selectedServer}
                            onChange={handleServerChange}
                            label="Server"
                            size="small"
                        >
                            {PROMETHEUS_SERVERS.map((server) => (
                                <MenuItem key={server.url} value={server.url}>
                                    {server.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                        sx={{
                            backgroundColor: "#667eea",
                            "&:hover": { backgroundColor: "#5568d3" },
                        }}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </Button>
                </Stack>
            </Stack>

            {lastUpdated && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Last updated: {lastUpdated.toLocaleString()}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6} lg={3}>
                    <MetricCard
                        title="CPU Usage"
                        icon={<SpeedIcon sx={{ fontSize: 32 }} />}
                        value={metrics.cpuUsage}
                        usage={metrics.cpuUsage}
                        unit="%"
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <MetricCard
                        title="Memory"
                        icon={<MemoryIcon sx={{ fontSize: 32 }} />}
                        value={metrics.memoryUsage}
                        total={`${(metrics.memoryTotal - metrics.memoryAvailable).toFixed(2)} GB / ${metrics.memoryTotal} GB`}
                        usage={metrics.memoryUsage}
                        unit="%"
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <MetricCard
                        title="Disk Usage"
                        icon={<StorageIcon sx={{ fontSize: 32 }} />}
                        value={metrics.diskUsage}
                        total={`${(metrics.diskTotal - metrics.diskAvailable).toFixed(2)} GB / ${metrics.diskTotal} GB`}
                        usage={metrics.diskUsage}
                        unit="%"
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <MetricCard
                        title="Network I/O"
                        icon={<NetworkIcon sx={{ fontSize: 32 }} />}
                        value={metrics.networkReceive}
                        total={`↓ ${metrics.networkReceive} KB/s | ↑ ${metrics.networkTransmit} KB/s`}
                        usage={Math.min((parseFloat(metrics.networkReceive) / 1024) * 100, 100)}
                        unit="KB/s"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <InfoCard
                        title="System Uptime"
                        icon={<ComputerIcon />}
                        value={`${metrics.uptime} days`}
                        subtitle="Since last reboot"
                        color="#3f51b5"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <InfoCard
                        title="Available Memory"
                        icon={<MemoryIcon />}
                        value={`${metrics.memoryAvailable} GB`}
                        subtitle={`Total: ${metrics.memoryTotal} GB`}
                        color="#4caf50"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <InfoCard
                        title="Available Disk"
                        icon={<StorageIcon />}
                        value={`${metrics.diskAvailable} GB`}
                        subtitle={`Total: ${metrics.diskTotal} GB`}
                        color="#ff9800"
                    />
                </Grid>
            </Grid>

            {/* History Chart Section */}
            <Box sx={{ mt: 4, mb: 4 }}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#2c3e50" }}>
                            CPU Usage History (Last 10 Days)
                        </Typography>
                        <Box sx={{ height: 400, width: "100%" }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    data={historyData}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#667eea" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="time"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                        unit="%"
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#fff',
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }}
                                        labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                                        formatter={(value) => [`${value}%`, 'CPU Usage']}
                                        labelFormatter={(label, payload) => {
                                            if (payload && payload.length > 0) {
                                                return payload[0].payload.fullTime;
                                            }
                                            return label;
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="usage"
                                        stroke="#667eea"
                                        strokeWidth={3}
                                        fillOpacity={1}
                                        fill="url(#colorUsage)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default SystemMonitoring;
